import { z } from 'zod';

/**
 * The contract a skill uses to say what each detail it emits means to an owner.
 * Skills declare; the platform-side resolver in `significance.ts` only reads the
 * generated binding. Nothing here is a source key, identity format or authority:
 * a declaration cannot make a change send, it can only say what a change is.
 */
export type FieldMeaningKind =
  | 'deadline'
  | 'appointment'
  | 'status'
  | 'description'
  | 'requirement'
  | 'amount'
  | 'mailing'
  | 'date'
  | 'detail';
export type ImmaterialReason = 'contact' | 'administrative' | 'identity';

export type FieldMeaningDeclaration =
  | { fields: readonly string[]; immaterial: ImmaterialReason }
  | {
      fields: readonly string[];
      kind: FieldMeaningKind;
      /** The detail as the subject of a sentence: "the time allowed to correct the violation". */
      noun: string;
      /** Why a change matters, as a predicate: "moves the deadline for the required work". */
      matters?: string;
      /** Full sentences for the model when the generated "This is …. A change …." reads badly. */
      means?: string;
      unit?: 'days';
    };

export type SkillFieldMeanings = {
  /** The observation source ids this skill emits (`SourceObservation.source`, `IssueSource`). */
  sources: readonly string[];
  declarations: readonly FieldMeaningDeclaration[];
};

const field = z.string().trim().min(1).max(120);
const sentence = z.string().trim().min(1).max(240);
export const fieldMeaningDeclarationSchema = z.union([
  z
    .object({
      fields: z.array(field).min(1),
      immaterial: z.enum(['contact', 'administrative', 'identity']),
    })
    .strict(),
  z
    .object({
      fields: z.array(field).min(1),
      kind: z.enum([
        'deadline',
        'appointment',
        'status',
        'description',
        'requirement',
        'amount',
        'mailing',
        'date',
        'detail',
      ]),
      noun: sentence,
      matters: sentence.optional(),
      means: z.string().trim().min(1).max(400).optional(),
      unit: z.literal('days').optional(),
    })
    .strict(),
]);
export const skillFieldMeaningsSchema = z
  .object({
    sources: z
      .array(
        z
          .string()
          .regex(/^[a-z][a-z0-9:-]*$/)
          .max(160),
      )
      .min(1),
    declarations: z.array(fieldMeaningDeclarationSchema).min(1),
  })
  .strict();

/** Source keys are matched case- and punctuation-insensitively; a label and its control id resolve alike. */
export function fieldMeaningKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Validates at import time so a bad declaration fails the registry build, never a notification. */
export function declareFieldMeanings(value: SkillFieldMeanings): SkillFieldMeanings {
  const parsed = skillFieldMeaningsSchema.parse(value);
  // A label and its control id may name the same detail; two meanings may not.
  const seen = new Map<string, FieldMeaningDeclaration>();
  for (const declaration of parsed.declarations)
    for (const name of declaration.fields) {
      const key = fieldMeaningKey(name);
      const previous = seen.get(key);
      if (previous !== undefined && previous !== declaration)
        throw new Error(`Field meaning declared twice with different meanings: ${name}`);
      seen.set(key, declaration);
    }
  return parsed;
}

export const contact = (...fields: string[]): FieldMeaningDeclaration => ({
  fields,
  immaterial: 'contact',
});
export const administrative = (...fields: string[]): FieldMeaningDeclaration => ({
  fields,
  immaterial: 'administrative',
});
export const identity = (...fields: string[]): FieldMeaningDeclaration => ({
  fields,
  immaterial: 'identity',
});
export const means = (
  kind: FieldMeaningKind,
  noun: string,
  fields: string[],
  options: { matters?: string; means?: string; unit?: 'days' } = {},
): FieldMeaningDeclaration => ({ fields, kind, noun, ...options });
